<script setup>
import { onLaunch, onShow } from '@dcloudio/uni-app';
import { tenantId as defaultTenantId } from '@/sheep/config';
import {
  parseFidFromUrl,
  restoreCrossDomainPaySessionFromUrl,
  restoreSharePromoFromCookie,
  restoreShopSessionFromCookie,
} from '@/sheep/api/zq/share';
import ZqApi from '@/sheep/api/zq';
import { consumePayResume } from '@/sheep/helper/zq-pay';
import { ensureSiteDomain } from '@/sheep/helper/zq-site-domain';

/** 扫码落地后立刻把 invite 解析成 wxfid，避免下单仍带上一家店 header */
async function syncPromoMerchant() {
  try {
    await ZqApi.resolvePromoMerchant();
  } catch (e) {
    // ignore：无 invite 或网络失败时不阻断启动
  }
}

function initTenantId() {
  let fallback = String(defaultTenantId ?? '1');
  if (typeof window !== 'undefined' && window.location?.search) {
    const urlTenant = new URLSearchParams(window.location.search).get('tenantId');
    if (urlTenant) {
      uni.setStorageSync('tenant-id', String(urlTenant));
      return;
    }
  }
  const cached = uni.getStorageSync('tenant-id');
  if (!cached) {
    uni.setStorageSync('tenant-id', fallback);
  }
}

/** 微信支付被微信踢回后，再进 H5：默认回首页（也可带 to=article 进方案） */
function resumePaidArticleIfNeeded() {
  let raw = null;
  try {
    raw = uni.getStorageSync('zq_pay_resume');
  } catch (e) {
    /* ignore */
  }
  // #ifdef H5
  if ((!raw || (!raw.articleId && raw.to !== 'home')) && typeof localStorage !== 'undefined') {
    try {
      const s = localStorage.getItem('zq_pay_resume') || sessionStorage.getItem('zq_pay_resume');
      if (s) raw = JSON.parse(s);
    } catch (e) {
      /* ignore */
    }
  }
  // #endif
  if (!raw || (!raw.articleId && raw.to !== 'home')) return;
  if (Date.now() - Number(raw.at || 0) > 10 * 60 * 1000) {
    try {
      uni.removeStorageSync('zq_pay_resume');
    } catch (e) {
      /* ignore */
    }
    return;
  }
  try {
    const pages = typeof getCurrentPages === 'function' ? getCurrentPages() : [];
    const cur = pages[pages.length - 1];
    const route = String(cur?.route || '');
    if (raw.to === 'home' || !raw.articleId) {
      if (route.includes('tabs/home') || route === 'pages/tabs/home') return;
    } else if (route.includes('gooddetail') && String(cur?.options?.id || '') === String(raw.articleId)) {
      return;
    }
  } catch (e) {
    /* ignore */
  }
  const resume = consumePayResume() || raw;
  try {
    uni.removeStorageSync('zq_pay_resume');
  } catch (e) {
    /* ignore */
  }
  const goHome = resume.to === 'home' || !resume.articleId;
  setTimeout(() => {
    if (goHome) {
      uni.switchTab({
        url: '/pages/tabs/home',
        fail: () => uni.reLaunch({ url: '/pages/tabs/home' }),
      });
      return;
    }
    const url = `/pages/shop/gooddetail?id=${encodeURIComponent(resume.articleId)}&from=joinpay${
      resume.orderId ? `&orderId=${encodeURIComponent(resume.orderId)}` : ''
    }`;
    uni.navigateTo({
      url,
      fail: () => uni.reLaunch({ url }),
    });
  }, 200);
}

onLaunch(() => {
  initTenantId();
  // 先恢复登录/店铺 cookie，再恢复推广码（www ↔ 主域）
  restoreShopSessionFromCookie();
  restoreSharePromoFromCookie();
  parseFidFromUrl();
  // 推广域支付跳主站：URL 带回 uid/openid/invite
  restoreCrossDomainPaySessionFromUrl();
  // 拉后台主站域名（OAuth / 分享兜底），不阻断启动
  ensureSiteDomain();
  syncPromoMerchant();
  resumePaidArticleIfNeeded();
});

onShow(() => {
  restoreShopSessionFromCookie();
  restoreSharePromoFromCookie();
  parseFidFromUrl();
  restoreCrossDomainPaySessionFromUrl();
  syncPromoMerchant();
  resumePaidArticleIfNeeded();
});
</script>

<style lang="scss">
@import '@/sheep/scss/index.scss';
@import '@/sheep/scss/zq-theme.scss';
</style>
